export type FiliereTarifDraftRow = {
  categorie: string;
  typeFrais: string;
  montantCdf: string;
  referenceUsd: string;
};

export type FiliereTarifsDraft = {
  enabled: boolean;
  rows: FiliereTarifDraftRow[];
};

const CATEGORIES = ['JEUNESSE_PRIORITAIRE', 'ADULTE_STANDARD', 'FONCTIONNAIRE_EMPLOYE'] as const;
const TYPES = ['INSCRIPTION', 'PARTICIPATION', 'BREVET'] as const;

export const FILIERE_TARIF_CATEGORIE_LABELS: Record<string, string> = {
  JEUNESSE_PRIORITAIRE: 'Jeunesse prioritaire',
  ADULTE_STANDARD: 'Adulte standard',
  FONCTIONNAIRE_EMPLOYE: 'Fonctionnaires & employés',
};

export const FILIERE_TARIF_TYPE_LABELS: Record<string, string> = {
  INSCRIPTION: 'Inscription',
  PARTICIPATION: 'Participation',
  BREVET: 'Brevet',
};

export function emptyFiliereTarifsDraft(): FiliereTarifsDraft {
  return {
    enabled: false,
    rows: CATEGORIES.flatMap((categorie) =>
      TYPES.map((typeFrais) => ({
        categorie,
        typeFrais,
        montantCdf: '',
        referenceUsd: '',
      })),
    ),
  };
}

export function buildFiliereTarifsDraftFromApi(payload: {
  utilisePersonnalises?: boolean;
  globaux?: Array<{
    categorie: string;
    typeFrais: string;
    montantCdf?: number | string;
    referenceUsd?: number | string | null;
  }>;
  personnalises?: Array<{
    categorie: string;
    typeFrais: string;
    montantCdf?: number | string;
    referenceUsd?: number | string | null;
  }>;
}): FiliereTarifsDraft {
  const enabled = Boolean(payload.utilisePersonnalises);
  const source = enabled && (payload.personnalises?.length ?? 0) > 0 ? payload.personnalises! : payload.globaux ?? [];
  const byKey = new Map(source.map((r) => [`${r.categorie}:${r.typeFrais}`, r] as const));

  return {
    enabled,
    rows: CATEGORIES.flatMap((categorie) =>
      TYPES.map((typeFrais) => {
        const row = byKey.get(`${categorie}:${typeFrais}`);
        return {
          categorie,
          typeFrais,
          montantCdf: row?.montantCdf != null ? String(row.montantCdf) : '',
          referenceUsd: row?.referenceUsd != null && row.referenceUsd !== '' ? String(row.referenceUsd) : '',
        };
      }),
    ),
  };
}

export function filiereTarifsDraftToPayload(draft: FiliereTarifsDraft) {
  if (!draft.enabled) return [];
  return draft.rows
    .map((row) => ({
      categorie: row.categorie,
      typeFrais: row.typeFrais,
      montantCdf: Number(row.montantCdf),
      referenceUsd: row.referenceUsd.trim() === '' ? null : Number(row.referenceUsd),
    }))
    .filter((row) => Number.isFinite(row.montantCdf) && row.montantCdf >= 0);
}

/** Merge global tarifs with filiere overrides (client-side for programme cards). */
export function mergeTarifsWithOverrides(
  globalRows: Array<{
    id?: string;
    categorie: string;
    typeFrais: string;
    montantCdf?: number | string;
    referenceUsd?: number | string | null;
  }>,
  overrides: Array<{
    idFiliere: string;
    categorie: string;
    typeFrais: string;
    montantCdf?: number | string;
    referenceUsd?: number | string | null;
  }>,
  idFiliere: string,
) {
  const forFiliere = overrides.filter((o) => o.idFiliere === idFiliere);
  if (forFiliere.length === 0) return globalRows;
  const byKey = new Map(forFiliere.map((o) => [`${o.categorie}:${o.typeFrais}`, o] as const));
  return globalRows.map((row) => {
    const o = byKey.get(`${row.categorie}:${row.typeFrais}`);
    if (!o) return row;
    return {
      ...row,
      montantCdf: o.montantCdf,
      referenceUsd: o.referenceUsd,
    };
  });
}
